import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Input,
  Textarea,
  Spinner,
  Alert,
  Checkbox,
  Select as MTSelect,
  Option,
  Collapse,
} from '@material-tailwind/react';
import Select from 'react-select';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { DocumentPlusIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';

import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import db from '../../firebase';
import { ReportEditor } from '../../components/report-editor';
import { generateDraft, generateMarketingUpdate, getDraft } from '../../services/reportApi';
import { UserAuth } from '../../context/AuthContext';
import RelistBreakdownDialog from '../../components/RelistBreakdownDialog';
import { UserData } from '../../context/UserContext';
import { DataContext } from '../../context/DataContext';

const REPORT_TYPES = [
  { value: 'market_valuation', label: 'Market Valuation' },
  { value: 'marketing_update', label: 'Marketing Update' },
];

const YEAR_RANGES = [
  { value: 0, label: 'Exact year' },
  { value: 1, label: '± 1 year' },
  { value: 2, label: '± 2 years' },
  { value: 3, label: '± 3 years' },
  { value: 4, label: '± 4 years' },
  { value: 5, label: '± 5 years' },
  { value: -1, label: 'All years' },
];

const PERIOD_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 120, label: '120 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
  { value: 730, label: '2 years' },
  { value: 1095, label: '3 years' },
];

const AVAILABLE_SECTIONS = [
  { id: 'cover', label: 'Cover' },
  { id: 'tableOfContents', label: 'TOC' },
  { id: 'about', label: 'About Lone Mountain' },
  { id: 'marketTrends', label: 'Trends' },
  { id: 'onMarketComps', label: 'On Market Comparables' },
  { id: 'recentlySoldComps', label: 'Recently Sold Comparables' },
  { id: 'summaryPricing', label: 'Summary' },
  { id: 'strategy', label: 'Services' },
  { id: 'assumptions', label: 'Thank You' },
];

const MARKETING_UPDATE_SECTIONS = [
  { id: 'cover', label: 'Cover' },
  { id: 'tableOfContents', label: 'TOC' },
  { id: 'listingPerformance', label: 'Listing Performance' },
  { id: 'marketTrends', label: 'Market Trends' },
  { id: 'onMarketComps', label: 'On Market Comps' },
  { id: 'recentlySoldComps', label: 'Recently Sold Comps' },
  { id: 'summaryPricing', label: 'Valuation Summary' },
  { id: 'valuationHistory', label: 'Valuation History' },
  { id: 'recommendations', label: 'Recommendations' },
  { id: 'assumptions', label: 'Thank You' },
];

const DEFAULT_VALUATION_SECTIONS = ['cover', 'tableOfContents', 'about', 'marketTrends', 'onMarketComps', 'recentlySoldComps', 'summaryPricing', 'strategy', 'assumptions'];
const DEFAULT_MARKETING_SECTIONS = ['cover', 'tableOfContents', 'listingPerformance', 'marketTrends', 'onMarketComps', 'recentlySoldComps', 'summaryPricing', 'valuationHistory', 'recommendations', 'assumptions'];

function ReportEditorPage() {
  const { user, delegateFor } = UserAuth();
  const { userData } = UserData();
  const { draftId } = useParams();
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentProfiles, setAgentProfiles] = useState({});
  // Get manufacturer/model data from context
  const dataContext = useContext(DataContext);
  const manufacturerData = dataContext.getManufacturerData();
  const modelData = dataContext.getModelData();

  const [draft, setDraft] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [error, setError] = useState(null);
  const [showSections, setShowSections] = useState(false);

  // Report type state
  const [reportType, setReportType] = useState('market_valuation');

  // Registration state (for marketing update)
  const [registrationOptions, setRegistrationOptions] = useState([]);
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [relistDom, setRelistDom] = useState(null);
  const [relistDialogOpen, setRelistDialogOpen] = useState(false);

  // Manufacturer/model select state
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  // Build manufacturer options from DataContext
  const manufacturerOptions = useMemo(() => {
    if (!Object.keys(manufacturerData).length) return [];
    return Object.keys(manufacturerData)
      .sort()
      .map((k) => ({ value: manufacturerData[k], label: k }));
  }, [manufacturerData]);

  // Build model options filtered by selected manufacturer
  const modelOptions = useMemo(() => {
    if (!Object.keys(modelData).length || !selectedManufacturer) return [];

    const options = [];
    for (const [key, value] of Object.entries(modelData)) {
      const manufacturersForModel = new Set(value.manufacturers);
      const manufacturerIds = new Set(selectedManufacturer.value?.ids || []);
      const hasIntersection = manufacturersForModel.intersection
        ? manufacturersForModel.intersection(manufacturerIds).size > 0
        : [...manufacturersForModel].some(id => manufacturerIds.has(id));

      if (hasIntersection) {
        options.push({ value: value, label: key });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [modelData, selectedManufacturer]);

  // Handle manufacturer change - clear model when manufacturer changes
  const handleManufacturerChange = (option) => {
    setSelectedManufacturer(option);
    setSelectedModel(null);
  };

  const [reportParams, setReportParams] = useState({
    customerName: '',
    subjectAircraftDetails: '',
    subjectAircraft: {
      registration: '',
      year: '',
      totalTime: '',
    },
    yearRange: 1,
    templateId: 'standard',
    includedSections: DEFAULT_VALUATION_SECTIONS,
    analysisConfig: {
      periodDays: 90,
    },
    preparer: {
      name: '',
      email: '',
      phone: '',
      title: '',
    },
    teamId: '',
    sharedWithTeam: false,
  });

  // Preload webstats registrations on mount (direct Firestore query)
  useEffect(() => {
    const loadRegistrations = async () => {
      setIsLoadingRegistrations(true);
      try {
        const snap = await getDocs(collection(db, 'webstats_cumulative'));
        const options = [];
        snap.docs.forEach((d) => {
          if (d.id === '_meta') return;
          const data = d.data();
          if (!data.reg) return;
          if ((data.status || '').toUpperCase() !== 'ACTIVE') return;
          const listedDate = data.ws_creation_date?.toDate
            ? data.ws_creation_date.toDate()
            : data.ws_creation_date ? new Date(data.ws_creation_date) : null;
          const listedStr = listedDate
            ? listedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : '';
          options.push({
            value: {
              reg: data.reg,
              listing_id: data.listing_id,
              year: data.year,
              manufacturer: data.manufacturer,
              model: data.model,
              category: data.category,
              days_aging: data.days_aging,
              true_dom: data.relist_true_dom != null ? data.relist_true_dom : data.days_aging,
              relist_count: data.relist_count || 1,
              ws_creation_date: data.ws_creation_date || null,
              listing_url: data.listing_url,
            },
            label: `${data.reg} — ${data.year || ''} ${data.manufacturer || ''} ${data.model || ''}${listedStr ? ` (listed ${listedStr})` : ''}`.trim(),
          });
        });
        options.sort((a, b) => (a.value.reg || '').localeCompare(b.value.reg || ''));
        setRegistrationOptions(options);
      } catch (err) {
        console.error('Failed to load registrations:', err);
      } finally {
        setIsLoadingRegistrations(false);
      }
    };
    loadRegistrations();
  }, []);

  // Load agent profiles for "on behalf of" selector
  useEffect(() => {
    if (!delegateFor || delegateFor.length === 0) return;
    const loadAgentProfiles = async () => {
      const profiles = {};
      for (const agentUid of delegateFor) {
        try {
          const docSnap = await getDoc(doc(db, "user_data", agentUid));
          if (docSnap.exists()) {
            profiles[agentUid] = { uid: agentUid, ...docSnap.data() };
          }
        } catch (err) {
          console.error(`Failed to load agent profile ${agentUid}:`, err);
        }
      }
      setAgentProfiles(profiles);
    };
    loadAgentProfiles();
  }, [delegateFor]);

  // Build agent options for the selector
  const agentOptions = useMemo(() => {
    return Object.values(agentProfiles).map((profile) => ({
      value: profile.uid,
      label: `${profile.displayName || 'Unknown'} (${profile.email || ''})`,
      profile,
    }));
  }, [agentProfiles]);

  // Auto-populate preparer and teamId from user profile or selected agent
  useEffect(() => {
    if (selectedAgent) {
      const agentProfile = agentProfiles[selectedAgent.value];
      if (agentProfile) {
        setReportParams((prev) => ({
          ...prev,
          preparer: {
            name: agentProfile.displayName || '',
            email: agentProfile.email || '',
            phone: agentProfile.phoneNumber || '',
            title: agentProfile.title || '',
            dateFormat: agentProfile.dateFormat || 'MM/DD/YYYY',
          },
          teamId: agentProfile.teamId || '',
          onBehalfOf: selectedAgent.value,
        }));
        return;
      }
    }
    setReportParams((prev) => ({
      ...prev,
      preparer: {
        name: user?.displayName || '',
        email: user?.email || '',
        phone: userData?.phoneNumber || '',
        title: userData?.title || '',
        dateFormat: userData?.dateFormat || 'MM/DD/YYYY',
      },
      teamId: userData?.teamId || '',
      onBehalfOf: undefined,
    }));
  }, [user?.displayName, user?.email, userData?.phoneNumber, userData?.title, userData?.teamId, userData?.dateFormat, selectedAgent, agentProfiles]);

  // Load existing draft if draftId is provided (wait for user auth)
  useEffect(() => {
    if (draftId && user) {
      const loadExistingDraft = async () => {
        try {
          setIsLoadingDraft(true);
          setError(null);
          const draftData = await getDraft(draftId);
          console.log('Loaded draft:', draftData);
          setDraft(draftData);
        } catch (err) {
          console.error('Failed to load draft:', err);
          setError(err.message);
        } finally {
          setIsLoadingDraft(false);
        }
      };
      loadExistingDraft();
    }
  }, [draftId, user]);

  // Handle report type change
  const handleReportTypeChange = (val) => {
    setReportType(val);
    setSelectedRegistration(null);
    if (val === 'marketing_update') {
      setReportParams((prev) => ({
        ...prev,
        includedSections: DEFAULT_MARKETING_SECTIONS,
      }));
    } else {
      setReportParams((prev) => ({
        ...prev,
        includedSections: DEFAULT_VALUATION_SECTIONS,
      }));
    }
  };

  // Handle registration selection (auto-populate aircraft details)
  const handleRegistrationChange = async (option) => {
    setSelectedRegistration(option);
    if (option?.value) {
      const data = option.value;
      setReportParams((prev) => ({
        ...prev,
        subjectAircraft: {
          ...prev.subjectAircraft,
          registration: data.reg || '',
          year: data.year ? String(data.year) : '',
        },
      }));
      // Find and set manufacturer/model from options if possible
      const mfr = manufacturerOptions.find((m) => m.label.toUpperCase() === (data.manufacturer || '').toUpperCase());
      if (mfr) {
        setSelectedManufacturer(mfr);
      }
      // Load relist cycle data for the breakdown dialog
      try {
        const cumQuery = query(collection(db, 'webstats_cumulative'), where('reg', '==', data.reg));
        const cumSnap = await getDocs(cumQuery);
        const numericId = data.listing_id;
        let match = null;
        const allDocs = [];
        cumSnap.forEach((d) => allDocs.push(d.data()));

        for (const cumData of allDocs) {
          if (cumData.relist_true_dom && cumData.relist_count > 1) {
            const cycleIds = cumData.relist_cycle_listing_ids || [];
            if (cycleIds.includes(numericId)) {
              const cycleListings = allDocs
                .filter((d) => cycleIds.includes(d.listing_id))
                .sort((a, b) => {
                  const aDate = a.ws_creation_date?.toDate ? a.ws_creation_date.toDate() : a.ws_creation_date ? new Date(a.ws_creation_date) : new Date(0);
                  const bDate = b.ws_creation_date?.toDate ? b.ws_creation_date.toDate() : b.ws_creation_date ? new Date(b.ws_creation_date) : new Date(0);
                  return aDate - bDate;
                });
              match = {
                trueDom: cumData.relist_true_dom,
                relistCount: cumData.relist_count,
                cycleViews: cumData.relist_cycle_views,
                cycleImpressions: cumData.relist_cycle_impressions,
                listings: cycleListings,
              };
              break;
            }
          } else if (!match && cumData.listing_id === numericId) {
            match = { trueDom: cumData.relist_true_dom || cumData.days_aging, relistCount: 1, listings: [cumData] };
          }
        }
        setRelistDom(match);
      } catch (err) {
        // Non-critical
      }
    }
  };

  const handleSectionToggle = (sectionId) => {
    setReportParams((prev) => {
      const sections = prev.includedSections.includes(sectionId)
        ? prev.includedSections.filter((s) => s !== sectionId)
        : [...prev.includedSections, sectionId];
      return { ...prev, includedSections: sections };
    });
  };

  const handleGenerateDraft = async () => {
    try {
      setIsGenerating(true);
      setError(null);

      let draftResponse;

      if (isMarketingUpdate) {
        const reg = selectedRegistration.value;
        const params = {
          customerName: reportParams.customerName,
          listingId: reg.listing_id,
          preparer: reportParams.preparer,
          analysisConfig: {
            periodDays: reportParams.analysisConfig.periodDays,
            yearRange: reportParams.yearRange,
          },
          templateId: reportParams.templateId,
          teamId: reportParams.teamId,
          sharedWithTeam: reportParams.sharedWithTeam,
          ...(reportParams.onBehalfOf ? { onBehalfOf: reportParams.onBehalfOf } : {}),
        };
        draftResponse = await generateMarketingUpdate(params);
      } else {
        const params = {
          customerName: reportParams.customerName,
          templateId: reportParams.templateId,
          includedSections: reportParams.includedSections,
          analysisConfig: {
            ...reportParams.analysisConfig,
            yearRange: reportParams.yearRange,
          },
          preparer: reportParams.preparer,
          teamId: reportParams.teamId,
          sharedWithTeam: reportParams.sharedWithTeam,
          ...(reportParams.onBehalfOf ? { onBehalfOf: reportParams.onBehalfOf } : {}),
          subjectAircraft: {
            registration: reportParams.subjectAircraft.registration,
            manufacturer: selectedManufacturer?.label || '',
            model: selectedModel?.label || '',
            year: parseInt(reportParams.subjectAircraft.year, 10),
            totalTime: parseInt(reportParams.subjectAircraft.totalTime, 10),
            additionalDetails: reportParams.subjectAircraftDetails,
          },
        };
        draftResponse = await generateDraft(params);
      }
      console.log('Draft response:', draftResponse);
      if (draftResponse.draftId) {
        setDraft(draftResponse);
      } else {
        setError('No draft ID returned from server');
      }
    } catch (err) {
      console.error('Generate draft error:', err);
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFinalized = async (result) => {
    console.log('Finalize result:', result);
    // Refresh draft data to get updated reportCount
    try {
      const updatedDraft = await getDraft(draftId || draft?.draftId);
      setDraft(updatedDraft);
    } catch (err) {
      console.error('Failed to refresh draft after finalization:', err);
    }
  };

  const isMarketingUpdate = reportType === 'marketing_update';

  const activeSections = isMarketingUpdate ? MARKETING_UPDATE_SECTIONS : AVAILABLE_SECTIONS;

  const isFormValid = isMarketingUpdate
    ? reportParams.customerName.trim() &&
      selectedRegistration &&
      reportParams.includedSections.length > 0
    : reportParams.customerName.trim() &&
      reportParams.templateId.trim() &&
      reportParams.includedSections.length > 0 &&
      reportParams.subjectAircraft.registration?.trim() &&
      selectedManufacturer?.label &&
      selectedModel?.label &&
      reportParams.subjectAircraft.year &&
      reportParams.subjectAircraft.totalTime;

  // Show loading state when loading existing draft
  if (isLoadingDraft) {
    return (
      <div className="p-6">
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-12">
            <Spinner className="h-8 w-8" />
            <Typography color="gray">Loading draft...</Typography>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Show report editor if draft exists
  if (draft) {
    return (
      <div className="p-6 h-[calc(100vh-100px)]">
        <ReportEditor draft={draft} onDraftChange={setDraft} onFinalized={handleFinalized} userData={userData} />
      </div>
    );
  }

  // Show draft generation form
  return (
    <div className="p-6">
      <Card className="max-w-2xl mx-auto">
        <CardBody className="space-y-6">
          <Typography variant="h5" color="blue-gray">
            Generate New Report
          </Typography>

          {error && (
            <Alert color="red" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Report Type */}
          <MTSelect
            label="Report Type"
            value={reportType}
            onChange={handleReportTypeChange}
          >
            {REPORT_TYPES.map((t) => (
              <Option key={t.value} value={t.value}>
                {t.label}
              </Option>
            ))}
          </MTSelect>

          {/* On behalf of - only shown for delegates */}
          {agentOptions.length > 0 && (
            <div>
              <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                Creating on behalf of
              </Typography>
              <Select
                options={[{ value: null, label: 'Myself' }, ...agentOptions]}
                value={selectedAgent || { value: null, label: 'Myself' }}
                onChange={(option) => setSelectedAgent(option?.value ? option : null)}
                placeholder="Select agent..."
                styles={{
                  control: (base) => ({ ...base, minHeight: '40px' }),
                }}
              />
            </div>
          )}

          {/* Customer */}
          <Input
            label="Customer Name *"
            value={reportParams.customerName}
            onChange={(e) =>
              setReportParams((prev) => ({
                ...prev,
                customerName: e.target.value,
              }))
            }
            placeholder="e.g., John Smith"
          />

          {/* Marketing Update: Registration dropdown */}
          {isMarketingUpdate && (
            <div>
              <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                Registration *
              </Typography>
              <Select
                options={registrationOptions}
                value={selectedRegistration}
                onChange={handleRegistrationChange}
                placeholder={isLoadingRegistrations ? "Loading registrations..." : "Search by registration..."}
                isLoading={isLoadingRegistrations}
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({ ...base, minHeight: '40px' }),
                }}
              />
              {selectedRegistration?.value && (
                <div className="mt-2 p-3 bg-blue-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-600">Year:</span> {selectedRegistration.value.year}</div>
                    <div><span className="text-gray-600">Manufacturer:</span> {selectedRegistration.value.manufacturer}</div>
                    <div><span className="text-gray-600">Model:</span> {selectedRegistration.value.model}</div>
                    {(() => {
                      const v = selectedRegistration.value;
                      let trueDom;
                      trueDom = v.true_dom || v.days_aging;
                      return (
                        <>
                          <div><span className="text-gray-600">True DOM:</span> <strong>{trueDom}d</strong></div>
                          <div><span className="text-gray-600">Controller DOM (this listing):</span> {v.days_aging}d</div>
                          {v.relist_count > 1 && (
                            <div>
                              <span className="text-gray-600">Relists:</span>{' '}
                              <span
                                className="text-amber-700 hover:text-amber-900 underline cursor-pointer"
                                onClick={() => setRelistDialogOpen(true)}
                              >
                                {v.relist_count} listings
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div><span className="text-gray-600">Category:</span> {selectedRegistration.value.category}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Subject Aircraft - only for market valuation */}
          {!isMarketingUpdate && (
            <div className="space-y-4">
              <div>
                <Typography variant="small" className="font-semibold text-blue-gray-700">
                  Subject Aircraft
                </Typography>
                <Typography variant="small" color="gray">
                  Enter details for market analysis
                </Typography>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Registration *"
                  value={reportParams.subjectAircraft.registration}
                  onChange={(e) =>
                    setReportParams((prev) => ({
                      ...prev,
                      subjectAircraft: {
                        ...prev.subjectAircraft,
                        registration: e.target.value.toUpperCase(),
                      },
                    }))
                  }
                  placeholder="N123AB"
                />
                <Input
                  type="number"
                  label="Year *"
                  value={reportParams.subjectAircraft.year}
                  onChange={(e) =>
                    setReportParams((prev) => ({
                      ...prev,
                      subjectAircraft: {
                        ...prev.subjectAircraft,
                        year: e.target.value,
                      },
                    }))
                  }
                  placeholder="2019"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                    Manufacturer *
                  </Typography>
                  <Select
                    options={manufacturerOptions}
                    value={selectedManufacturer}
                    onChange={handleManufacturerChange}
                    placeholder="Select..."
                    isClearable
                    styles={{
                      control: (base) => ({ ...base, minHeight: '40px' }),
                    }}
                  />
                </div>
                <div>
                  <Typography variant="small" color="blue-gray" className="mb-1 font-medium">
                    Model *
                  </Typography>
                  <Select
                    options={modelOptions}
                    value={selectedModel}
                    onChange={setSelectedModel}
                    placeholder={selectedManufacturer ? "Select..." : "Select manufacturer first"}
                    isDisabled={!selectedManufacturer}
                    isClearable
                    styles={{
                      control: (base) => ({ ...base, minHeight: '40px' }),
                    }}
                  />
                </div>
              </div>

              <Input
                type="number"
                label="Total Time (hours) *"
                value={reportParams.subjectAircraft.totalTime}
                onChange={(e) =>
                  setReportParams((prev) => ({
                    ...prev,
                    subjectAircraft: {
                      ...prev.subjectAircraft,
                      totalTime: e.target.value,
                    },
                  }))
                }
                placeholder="1200"
              />

              <Textarea
                label="Additional Details (equipment, upgrades, condition...)"
                value={reportParams.subjectAircraftDetails}
                onChange={(e) =>
                  setReportParams((prev) => ({
                    ...prev,
                    subjectAircraftDetails: e.target.value,
                  }))
                }
                rows={2}
              />
            </div>
          )}

          {/* Analysis Settings */}
          <div className="grid grid-cols-2 gap-4">
              <MTSelect
                label="Year Range"
                value={String(reportParams.yearRange)}
                onChange={(val) =>
                  setReportParams((prev) => ({
                    ...prev,
                    yearRange: parseInt(val, 10),
                  }))
                }
              >
                {YEAR_RANGES.map((r) => (
                  <Option key={String(r.value)} value={String(r.value)}>
                    {r.label}
                  </Option>
                ))}
              </MTSelect>
              <MTSelect
                label="Analysis Period"
                value={String(reportParams.analysisConfig.periodDays)}
                onChange={(val) =>
                  setReportParams((prev) => ({
                    ...prev,
                    analysisConfig: {
                      ...prev.analysisConfig,
                      periodDays: parseInt(val, 10),
                    },
                  }))
                }
              >
                {PERIOD_OPTIONS.map((p) => (
                  <Option key={p.value} value={String(p.value)}>
                    {p.label}
                  </Option>
                ))}
              </MTSelect>
            </div>

          {/* Sections */}
          <div className="border rounded-lg">
            <div
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
              onClick={() => setShowSections(!showSections)}
            >
              <div className="flex items-center gap-2">
                <Typography variant="small" className="font-medium">
                  Report Sections
                </Typography>
                <Typography variant="small" color="gray">
                  ({reportParams.includedSections.length}/{activeSections.length})
                </Typography>
              </div>
              {showSections ? (
                <ChevronUpIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              )}
            </div>
            <Collapse open={showSections}>
              <div className="grid grid-cols-2 gap-2 p-3 pt-0 border-t">
                {activeSections.map((section) => (
                  <Checkbox
                    key={section.id}
                    label={section.label}
                    checked={reportParams.includedSections.includes(section.id)}
                    onChange={() => handleSectionToggle(section.id)}
                  />
                ))}
              </div>
            </Collapse>
          </div>

          <Button
            className="flex items-center justify-center gap-2"
            onClick={handleGenerateDraft}
            disabled={isGenerating || !isFormValid}
            fullWidth
          >
            {isGenerating ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <DocumentPlusIcon className="h-5 w-5" />
            )}
            {isGenerating ? 'Generating...' : 'Generate Report'}
          </Button>
        </CardBody>
      </Card>

      <RelistBreakdownDialog
        open={relistDialogOpen}
        onClose={() => setRelistDialogOpen(false)}
        relistDom={relistDom}
        reg={selectedRegistration?.value?.reg}
        title={`${selectedRegistration?.value?.year || ''} ${selectedRegistration?.value?.manufacturer || ''} ${selectedRegistration?.value?.model || ''}`.trim()}
        currentListingId={selectedRegistration?.value?.listing_id}
      />
    </div>
  );
}

export default ReportEditorPage;
